import React, { useState, useEffect, useCallback } from 'react';
import { Button, Card, Spin, message, Form, Input, Select, DatePicker, Flex } from 'antd';
import { ArrowLeftOutlined, FilePdfOutlined } from '@ant-design/icons';
import fileDownload from "js-file-download";
import { CKEditor } from 'ckeditor4-react';
import { useNavigate, useParams } from 'react-router-dom';
import * as actions from "store/actions/AuthActions";
import axios from "axios";
import dayjs from "dayjs";
import { compactToolbar } from "utilities/config";

const { TextArea } = Input;
const { Option } = Select;

const editorConfig = {
  extraPlugins: ["tableresize", "uploadimage"],
  removePlugins: ["exportpdf", "forms", "smiley", "language", "iframe", "about"],
  filebrowserImageUploadUrl: process.env.REACT_APP_BASE_URL + `/template/upload_template_image/`,
  fileTools_requestHeaders: axios.defaults.headers.common,
  uploadUrl: process.env.REACT_APP_BASE_URL + `/template/upload_template_image/`,
  contentsCss: [
    process.env.REACT_APP_BASE_URL + "/staticfiles/ckeditor/4.19.0/full-all/document-style.css",
    "/ckeditor-content.css",
],
  bodyClass: "document-editor",
  height: "500px",
  width: "100%",
  allowedContent: true,
  protectedSource: [/{%[\s\S]*?%}+/g, /{{[\s\S]*?}}+/g],
  // Phones get a short toolbar; desktop keeps the full grouped one.
  ...compactToolbar(),
};

const ContractEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [contract, setContract] = useState(null);
  const [htmlContent, setHtmlContent] = useState('');
  const [contractTypes, setContractTypes] = useState([]);
  const [editorReady, setEditorReady] = useState(false);

  // Employees and accounts for counterparty dropdowns
  const [employees, setEmployees] = useState([]);
  const [accounts, setAccounts] = useState([]);

  const fetchContractTypesForDropdown = async () => {
    try {
      const response = await actions.loadContractTypes(1, "", 1000);
      if (response.status === 200) {
        setContractTypes(response.data.results || []);
      }
    } catch (e) {
      console.error("Error fetching contract types:", e);
    }
  };

  const fetchEmployeesForDropdown = async () => {
    try {
      const response = await actions.loadAllEmployees("");
      if (response.status === 200) {
        setEmployees(response.data.results || []);
      }
    } catch (e) {
      console.error("Error fetching employees:", e);
    }
  };

  const fetchAccountsForDropdown = async () => {
    try {
      const response = await actions.loadAllAccounts("");
      if (response.status === 200) {
        setAccounts(response.data.results || []);
      }
    } catch (e) {
      console.error("Error fetching accounts:", e);
    }
  };

  const fetchContractData = useCallback(async () => {
    setLoading(true);
    try {
      const contractResponse = await actions.fetchContract(id);
      if (contractResponse) {
        setContract(contractResponse);

        // Set form values with date conversion
        const formValues = {
          ...contractResponse,
          contract_effective_date: contractResponse.contract_effective_date
            ? dayjs(contractResponse.contract_effective_date) : null,
          contract_end_date: contractResponse.contract_end_date
            ? dayjs(contractResponse.contract_end_date) : null,
        };
        form.setFieldsValue(formValues);

        // If contract has html_content, use it; otherwise use contract_type_data.html_code
        if (contractResponse.html_content) {
          setHtmlContent(contractResponse.html_content);
        } else if (contractResponse.contract_type_data?.html_code) {
          // Use contract_type_data from API response (no separate fetch needed)
          setHtmlContent(contractResponse.contract_type_data.html_code);
        }
        setEditorReady(true);
      }
    } catch (error) {
      console.error("Error fetching contract:", error);
      message.error("Failed to load contract");
    }
    setLoading(false);
  }, [id, form]);

  useEffect(() => {
    fetchContractTypesForDropdown();
    fetchEmployeesForDropdown();
    fetchAccountsForDropdown();
    fetchContractData();
  }, [fetchContractData]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const submitData = {
        ...values,
        html_content: htmlContent,
        contract_effective_date: values.contract_effective_date?.format('YYYY-MM-DD') || null,
        contract_end_date: values.contract_end_date?.format('YYYY-MM-DD') || null,
      };

      await actions.updateContract(submitData, id);
      message.success('Contract saved successfully');
    } catch (error) {
      if (!error?.errorFields) {
        message.error('Failed to save contract');
      }
    } finally {
      setSaving(false);
    }
  };

  // Save first so the PDF reflects what's on screen, then export and download.
  const [exporting, setExporting] = useState(false);
  const handleSaveAndExport = async () => {
    try {
      const values = await form.validateFields();
      setExporting(true);
      await actions.updateContract({
        ...values,
        html_content: htmlContent,
        contract_effective_date: values.contract_effective_date?.format('YYYY-MM-DD') || null,
        contract_end_date: values.contract_end_date?.format('YYYY-MM-DD') || null,
      }, id);
      const exported = await actions.exportContractToPdf(id);
      const file = await actions.downloadContractPdf(id);
      fileDownload(file.data, exported.data.file_name);
      message.success('Contract saved and PDF downloaded');
    } catch (error) {
      if (!error?.errorFields) {
        message.error(error?.response?.data?.error || 'Failed to export PDF');
      }
    } finally {
      setExporting(false);
    }
  };

  const handleSaveAndExit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const submitData = {
        ...values,
        html_content: htmlContent,
        contract_effective_date: values.contract_effective_date?.format('YYYY-MM-DD') || null,
        contract_end_date: values.contract_end_date?.format('YYYY-MM-DD') || null,
      };

      await actions.updateContract(submitData, id);
      message.success('Contract saved successfully');
      navigate('/contract');
    } catch (error) {
      if (!error?.errorFields) {
        message.error('Failed to save contract');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 20 }}>
      <Card
        title={`Edit Contract: ${contract?.title || ''}`}
        extra={
          <div style={{ display: 'flex', gap: 10 }}>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/contract')}>
              Back to Contracts
            </Button>
            <Button onClick={handleSave} loading={saving}>Save</Button>
            <Button icon={<FilePdfOutlined />} onClick={handleSaveAndExport} loading={exporting}>Save & Export PDF</Button>
            <Button type="primary" onClick={handleSaveAndExit} loading={saving}>Save & Exit</Button>
          </div>
        }
      >
        <Form form={form} layout="vertical">
          {/* Basic Info */}
          <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Please enter contract title' }]}>
            <Input placeholder="Enter contract title" />
          </Form.Item>

          <Flex gap="middle">
            <Form.Item name="contract_type" label="Contract Type" style={{ flex: 1 }}>
              <Select placeholder="Select contract type" disabled>
                {contractTypes.map(type => (
                  <Option key={type.id} value={type.id}>{type.name}</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="status" label="Status" rules={[{ required: true }]} style={{ flex: 1 }}>
              <Select placeholder="Select status">
                <Option value="DRAFT">Draft</Option>
                <Option value="ACTIVE">Active</Option>
                <Option value="EXPIRED">Expired</Option>
                <Option value="TERMINATED">Terminated</Option>
              </Select>
            </Form.Item>
          </Flex>

          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Enter description (optional)" />
          </Form.Item>

          {/* Requester Info */}
          <Flex gap="middle">
            <Form.Item name="requester_name" label="Requester Name" style={{ flex: 1 }}>
              <Input placeholder="Enter requester name" />
            </Form.Item>
            <Form.Item name="requester_department" label="Requester Department" style={{ flex: 1 }}>
              <Input placeholder="Enter requester department" />
            </Form.Item>
          </Flex>

          {/* Counterparty Info */}
          <Form.Item name="counterparty_name" label="Counterparty Name" rules={[{ required: true }]}>
            <Input placeholder="Enter counterparty name" />
          </Form.Item>

          <Flex gap="middle">
            <Form.Item name="counterparty_contact" label="Counterparty Contact" style={{ flex: 1 }}>
              <Input placeholder="Enter contact info" />
            </Form.Item>
            <Form.Item name="counterparty_primary_contact" label="Primary Contact" style={{ flex: 1 }}>
              <Input placeholder="Enter primary contact" />
            </Form.Item>
          </Flex>

          {/* Counterparty Employee */}
          <Flex gap="middle">
            <Form.Item name="counterparty_employee" label="Counterparty Employee" style={{ flex: 1 }}>
              <Select
                placeholder="Select employee"
                allowClear
                showSearch
                optionFilterProp="children"
                onChange={(value) => {
                  if (value) {
                    const selectedEmployee = employees.find(e => e.id === value);
                    // Priority: personal_email > user_email > empty
                    const email = selectedEmployee?.personal_email || selectedEmployee?.user_email || '';
                    form.setFieldsValue({ counterparty_employee_email: email });
                  } else {
                    form.setFieldsValue({ counterparty_employee_email: '' });
                  }
                }}
              >
                {employees.map(emp => (
                  <Option key={emp.id} value={emp.id}>
                    {emp.user_name} ({emp.employee_id})
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="counterparty_employee_email" label="Employee Email" style={{ flex: 1 }}>
              <Input placeholder="Auto-filled from selected employee" />
            </Form.Item>
          </Flex>

          {/* Counterparty Account */}
          <Flex gap="middle">
            <Form.Item name="counterparty_account" label="Counterparty Account" style={{ flex: 1 }}>
              <Select
                placeholder="Select account"
                allowClear
                showSearch
                optionFilterProp="children"
                onChange={(value) => {
                  if (value) {
                    const selectedAccount = accounts.find(a => a.id === value);
                    form.setFieldsValue({
                      counterparty_account_email: selectedAccount?.email || ''
                    });
                  } else {
                    form.setFieldsValue({ counterparty_account_email: '' });
                  }
                }}
              >
                {accounts.map(acc => (
                  <Option key={acc.id} value={acc.id}>
                    {acc.name} ({acc.account_number})
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="counterparty_account_email" label="Account Email" style={{ flex: 1 }}>
              <Input placeholder="Auto-filled from selected account" />
            </Form.Item>
          </Flex>

          {/* Contract Dates with validation */}
          <Flex gap="middle">
            <Form.Item name="contract_effective_date" label="Effective Date" style={{ flex: 1 }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="contract_end_date"
              label="End Date"
              style={{ flex: 1 }}
              dependencies={['contract_effective_date']}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const effectiveDate = getFieldValue('contract_effective_date');
                    if (!value || !effectiveDate) return Promise.resolve();
                    if (value.isBefore(effectiveDate, 'day')) {
                      return Promise.reject(new Error('End date must be same or after effective date'));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Flex>

          {/* Contract Terms */}
          <Flex gap="middle">
            <Form.Item name="contract_term" label="Contract Term" style={{ flex: 1 }}>
              <Select placeholder="Select contract term">
                <Option value="DEFINITE">Definite</Option>
                <Option value="INDEFINITE">Indefinite</Option>
              </Select>
            </Form.Item>
            <Form.Item name="termination_notice_period" label="Termination Notice Period" style={{ flex: 1 }}>
              <Input placeholder="e.g., 30 days" />
            </Form.Item>
          </Flex>
        </Form>

        {/* CKEditor for HTML Content */}
        <div style={{ marginTop: 20 }}>
          <label style={{ fontWeight: 'bold', marginBottom: 8, display: 'block' }}>Contract Content</label>
          {editorReady && (
            <CKEditor
              key={`editor-${id}`}
              initData={htmlContent}
              config={editorConfig}
              editorUrl={process.env.REACT_APP_BASE_URL + "/staticfiles/ckeditor/4.19.0/full-all/ckeditor.js"}
              onChange={(event) => {
                const data = event.editor.getData();
                setHtmlContent(data);
              }}
            />
          )}
        </div>
      </Card>
    </div>
  );
};

export default ContractEditor;
